import { useEffect, useRef, type ReactNode } from 'react';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Max-height as CSS value. Defaults to 88vh on mobile / 80vh on desktop. */
  maxHeight?: string;
  /** Width preset for desktop. */
  size?: 'sm' | 'md' | 'lg';
  /** When true, no grabber bar is rendered (use for confirmation-style sheets). */
  noGrabber?: boolean;
  /** ARIA label for the dialog. */
  label?: string;
}

const SIZE_CLASS: Record<NonNullable<SheetProps['size']>, string> = {
  sm: 'nav:max-w-md',
  md: 'nav:max-w-lg',
  lg: 'nav:max-w-2xl',
};

/**
 * Responsive modal sheet with accessible keyboard focus trap and body scroll lock.
 */
export function Sheet({
  open,
  onClose,
  children,
  maxHeight,
  size = 'md',
  noGrabber = false,
  label,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const previousActiveElement = document.activeElement as HTMLElement | null;
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKey);

    // Initial focus into panel
    const timer = setTimeout(() => {
      if (panelRef.current) {
        const firstFocusable = panelRef.current.querySelector<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
        );
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          panelRef.current.focus();
        }
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = origOverflow;
      window.removeEventListener('keydown', onKey);
      previousActiveElement?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex items-end justify-center nav:items-center"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]"
      />

      {/* Sheet panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={[
          'relative w-full overflow-hidden outline-none',
          SIZE_CLASS[size],
          'bg-bg shadow-[0_-12px_40px_rgba(33,28,21,0.18)]',
          'rounded-t-3xl nav:rounded-3xl',
          'flex flex-col',
          'bs-sheet-enter',
        ].join(' ')}
        style={{ maxHeight: maxHeight ?? 'min(88vh, calc(100dvh - env(safe-area-inset-top) - 32px))' }}
      >
        {!noGrabber && (
          <div className="flex justify-center pt-1 pb-3.5 shrink-0">
            <span className="block h-1 w-9 rounded-sm bg-line" />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
